<script lang="ts">
    import { onMount } from "svelte";
    import { modals } from "@wa-modals";
    import { localUserStore } from "../../Connection/LocalUserStore";
    import { ABSOLUTE_PUSHER_URL } from "../../Enum/ComputedConst";

    type PresencePeriod = {
        start: number;
        end: number | null;
        durationMs: number;
    };

    type PresenceUserSummary = {
        uuid: string;
        name: string;
        currentState: string;
        activationState: string;
        agentAvailable: boolean | null;
        lastUpdate: number | null;
        absentDurationMs: number;
        absenceCount: number;
        lastAbsence: PresencePeriod | null;
    };

    type PresenceResponse = {
        from: number;
        to: number;
        generatedAt: number;
        users: PresenceUserSummary[];
    };

    type Period = "today" | "yesterday" | "7days" | "30days";

    let period = $state<Period>("today");
    let selectedUser = $state("");
    let users = $state<PresenceUserSummary[]>([]);
    let loading = $state(true);
    let error = $state<string | null>(null);
    let generatedAt = $state<number | null>(null);

    function startOfDay(date: Date): number {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }

    function getInterval(): { from: number; to: number } {
        const now = new Date();
        const today = startOfDay(now);

        if (period === "yesterday") {
            return {
                from: today - 24 * 60 * 60 * 1000,
                to: today - 1,
            };
        }

        if (period === "7days") {
            return {
                from: today - 6 * 24 * 60 * 60 * 1000,
                to: Date.now(),
            };
        }

        if (period === "30days") {
            return {
                from: today - 29 * 24 * 60 * 60 * 1000,
                to: Date.now(),
            };
        }

        return {
            from: today,
            to: Date.now(),
        };
    }

    async function loadPresenceHistory(): Promise<void> {
        loading = true;
        error = null;

        try {
            const token = localUserStore.getAuthToken();

            if (!token) {
                error = "Sessão não encontrada.";
                users = [];
                return;
            }

            const { from, to } = getInterval();

            const url = new URL("presence-history", ABSOLUTE_PUSHER_URL);
            url.searchParams.set("from", from.toString());
            url.searchParams.set("to", to.toString());

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: {
                    Authorization: token,
                },
                cache: "no-store",
            });

            if (response.status === 403) {
                error = "Você não possui permissão para visualizar o painel de presença.";
                users = [];
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = (await response.json()) as PresenceResponse;

            users = data.users;
            generatedAt = data.generatedAt;

            if (selectedUser && !users.some((user) => user.uuid === selectedUser)) {
                selectedUser = "";
            }
        } catch (e) {
            console.error("[PresenceHistoryPanel] Failed to load presence history", e);
            error = "Não foi possível carregar o histórico de presença.";
            users = [];
        } finally {
            loading = false;
        }
    }

    function formatDuration(durationMs: number): string {
        const totalSeconds = Math.max(0, Math.floor(durationMs / 1_000));

        if (totalSeconds < 60) {
            return `${totalSeconds}s`;
        }

        const totalMinutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        if (totalMinutes < 60) {
            return `${totalMinutes}min ${seconds}s`;
        }

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}min`;
    }

    function formatTime(timestamp: number | null): string {
        if (timestamp === null) {
            return "—";
        }

        return new Date(timestamp).toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    }

    function formatLastAbsence(absencePeriod: PresencePeriod | null): string {
        if (!absencePeriod) {
            return "—";
        }

        if (absencePeriod.end === null) {
            if (period === "yesterday") {
                return `${formatTime(absencePeriod.start)} → 23:59:59`;
            }
            return `${formatTime(absencePeriod.start)} → agora`;
        }

        return `${formatTime(absencePeriod.start)} → ${formatTime(absencePeriod.end)}`;
    }

    function statusLabel(user: PresenceUserSummary): string {
        if (user.activationState === "disabled") {
            return "Monitoramento desativado";
        }

        if (user.activationState === "error") {
            return "Erro no monitoramento";
        }

        if (user.currentState === "absent") {
            return "Ausente";
        }

        if (user.currentState === "present") {
            return "Presente";
        }

        return "Sem informação";
    }

    function statusClass(user: PresenceUserSummary): string {
        if (user.currentState === "absent" && user.activationState === "enabled") {
            return "status-away";
        }

        if (user.currentState === "present" && user.activationState === "enabled") {
            return "status-online";
        }

        return "status-unknown";
    }

    function activationLabel(state: string): string {
        if (state === "enabled") return "Ativa";
        if (state === "disabled") return "Desativada";
        if (state === "error") return "Erro";
        return "—";
    }

    function agentLabel(value: boolean | null): string {
        if (value === true) return "Ativo";
        if (value === false) return "Não detectado";
        return "—";
    }

    let visibleUsers = $derived(
        selectedUser ? users.filter((user) => user.uuid === selectedUser) : users,
    );

    function close(): void {
        modals.close();
    }

    onMount(() => {
        void loadPresenceHistory();

        const timer = window.setInterval(() => {
            void loadPresenceHistory();
        }, 30_000);

        return () => window.clearInterval(timer);
    });
</script>

<div
    class="fixed inset-0 z-[2001] flex items-center justify-center p-4 pointer-events-auto bg-black/50"
    role="dialog"
    aria-modal="true"
    aria-labelledby="presence-panel-title"
>
    <button
        type="button"
        class="absolute inset-0 w-full h-full cursor-default"
        aria-label="Fechar painel de presença"
        onclick={() => modals.close()}
    ></button>

    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div
        class="presence-panel relative pointer-events-auto"
        onclick={(event) => {
            event.stopPropagation();
        }}
    >
    <div class="presence-header">
        <div>
            <h2 id="presence-panel-title">Painel de Presença</h2>
            <p>Acompanhamento de presença automática e períodos de ausência</p>
        </div>

        <button class="close-button" onclick={close} title="Fechar">×</button>
    </div>

    <div class="filters">
        <div class="period-buttons">
            <button
                class:active={period === "today"}
                onclick={() => {
                    period = "today";
                    void loadPresenceHistory();
                }}>Hoje</button
            >

            <button
                class:active={period === "yesterday"}
                onclick={() => {
                    period = "yesterday";
                    void loadPresenceHistory();
                }}>Ontem</button
            >

            <button
                class:active={period === "7days"}
                onclick={() => {
                    period = "7days";
                    void loadPresenceHistory();
                }}>7 dias</button
            >

            <button
                class:active={period === "30days"}
                onclick={() => {
                    period = "30days";
                    void loadPresenceHistory();
                }}>30 dias</button
            >
        </div>

        <select bind:value={selectedUser}>
            <option value="">Todos os colaboradores</option>
            {#each users as user}
                <option value={user.uuid}>{user.name}</option>
            {/each}
        </select>

        <button class="refresh-button" onclick={() => void loadPresenceHistory()}>
            Atualizar
        </button>
    </div>

    {#if generatedAt !== null}
        <div class="updated">
            Atualizado às {formatTime(generatedAt)}
        </div>
    {/if}

    {#if loading && users.length === 0}
        <div class="state-message">Carregando dados de presença...</div>
    {:else if error}
        <div class="state-message error">{error}</div>
    {:else if visibleUsers.length === 0}
        <div class="state-message">Nenhum registro de presença encontrado neste período.</div>
    {:else}
        <div class="summary">
            <div>
                <strong>{visibleUsers.length}</strong>
                <span>colaboradores</span>
            </div>

            <div>
                <strong>
                    {visibleUsers.filter(
                        (user) => user.currentState === "present" && user.activationState === "enabled",
                    ).length}
                </strong>
                <span>presentes</span>
            </div>

            <div>
                <strong>
                    {visibleUsers.filter(
                        (user) => user.currentState === "absent" && user.activationState === "enabled",
                    ).length}
                </strong>
                <span>ausentes</span>
            </div>

            <div>
                <strong>
                    {formatDuration(
                        visibleUsers.reduce((total, user) => total + user.absentDurationMs, 0),
                    )}
                </strong>
                <span>ausência acumulada</span>
            </div>
        </div>

        <div class="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th>Colaborador</th>
                        <th>Status</th>
                        <th>Ausente no período</th>
                        <th>Períodos</th>
                        <th>Última ausência</th>
                        <th>Agente Windows</th>
                        <th>Presença automática</th>
                        <th>Última atualização</th>
                    </tr>
                </thead>

                <tbody>
                    {#each visibleUsers as user}
                        <tr>
                            <td class="name">{user.name}</td>

                            <td>
                                <span class="status {statusClass(user)}">
                                    {statusLabel(user)}
                                </span>
                            </td>

                            <td>{formatDuration(user.absentDurationMs)}</td>

                            <td>{user.absenceCount}</td>

                            <td>{formatLastAbsence(user.lastAbsence)}</td>

                            <td>{agentLabel(user.agentAvailable)}</td>

                            <td>{activationLabel(user.activationState)}</td>

                            <td>{formatTime(user.lastUpdate)}</td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
    </div>
</div>

<style>
    .presence-panel {
        width: min(1180px, calc(100vw - 40px));
        max-height: calc(100vh - 80px);
        overflow: auto;
        background: #202124;
        color: white;
        border-radius: 14px;
        padding: 22px;
        box-shadow: 0 20px 60px rgb(0 0 0 / 45%);
    }

    .presence-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 20px;
        margin-bottom: 20px;
    }

    .presence-header h2 {
        margin: 0;
        font-size: 24px;
    }

    .presence-header p {
        margin: 5px 0 0;
        opacity: 0.65;
        font-size: 13px;
    }

    .close-button {
        border: 0;
        background: transparent;
        color: white;
        font-size: 30px;
        line-height: 1;
        cursor: pointer;
        opacity: 0.75;
    }

    .close-button:hover {
        opacity: 1;
    }

    .filters {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;
        margin-bottom: 8px;
    }

    .period-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .period-buttons button,
    .refresh-button,
    select {
        border: 1px solid rgb(255 255 255 / 15%);
        background: rgb(255 255 255 / 7%);
        color: white;
        border-radius: 8px;
        padding: 8px 11px;
        font-size: 13px;
    }

    .period-buttons button,
    .refresh-button {
        cursor: pointer;
    }

    .period-buttons button:hover,
    .refresh-button:hover {
        background: rgb(255 255 255 / 13%);
    }

    .period-buttons button.active {
        background: white;
        color: #202124;
    }

    select {
        min-width: 220px;
    }

    select option {
        color: #202124;
    }

    .updated {
        font-size: 11px;
        opacity: 0.5;
        margin: 7px 0 15px;
    }

    .summary {
        display: grid;
        grid-template-columns: repeat(4, minmax(130px, 1fr));
        gap: 10px;
        margin-bottom: 16px;
    }

    .summary > div {
        background: rgb(255 255 255 / 6%);
        border: 1px solid rgb(255 255 255 / 8%);
        border-radius: 10px;
        padding: 14px;
    }

    .summary strong {
        display: block;
        font-size: 19px;
        margin-bottom: 3px;
    }

    .summary span {
        font-size: 11px;
        opacity: 0.6;
    }

    .table-wrapper {
        overflow-x: auto;
        border: 1px solid rgb(255 255 255 / 9%);
        border-radius: 10px;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        min-width: 1000px;
    }

    th,
    td {
        padding: 11px 12px;
        text-align: left;
        border-bottom: 1px solid rgb(255 255 255 / 7%);
        font-size: 12px;
        white-space: nowrap;
    }

    th {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.55;
        background: rgb(255 255 255 / 4%);
    }

    td.name {
        font-weight: 600;
    }

    tbody tr:last-child td {
        border-bottom: 0;
    }

    .status {
        display: inline-flex;
        align-items: center;
        padding: 4px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
    }

    .status-online {
        background: rgb(34 197 94 / 18%);
        color: #86efac;
    }

    .status-away {
        background: rgb(245 158 11 / 18%);
        color: #fcd34d;
    }

    .status-unknown {
        background: rgb(148 163 184 / 15%);
        color: #cbd5e1;
    }

    .state-message {
        padding: 40px;
        text-align: center;
        opacity: 0.65;
    }

    .state-message.error {
        color: #fca5a5;
        opacity: 1;
    }

    @media (max-width: 760px) {
        .presence-panel {
            width: calc(100vw - 20px);
            padding: 14px;
        }

        .summary {
            grid-template-columns: repeat(2, 1fr);
        }

        .filters,
        select {
            width: 100%;
        }
    }
</style>
